import { styled } from "goober";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { playClick } from "../lib/sounds";
import { userDisplayName } from "../lib/userDisplayName";
import { ConnectorLogo } from "./ConnectorLogo";
import { Row } from "./ConnectorDropdown";
import { Icon } from "./Icon";
import { Text } from "./Text";

export type CreatedBy =
  | { name: string; email: string }
  | readonly { name: string; email: string }[]
  | null;

export type AppInfo = ConnectorWithOAuth & { createdBy?: CreatedBy };

function createdByName(createdBy: CreatedBy | undefined): string | null {
  if (!createdBy) return null;
  if (Array.isArray(createdBy)) {
    const first = createdBy[0];
    return first ? userDisplayName(first) : null;
  }
  return userDisplayName(createdBy as { name: string; email: string });
}

const LeftIcon = styled("div")`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

type Props = {
  name: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
  size?: number;
  grayscale?: boolean;
} & (
  | { app: ConnectorWithOAuth; template?: never }
  | { app?: never; template: { name: string; identifier: string } }
);

export function ConnectorRow({
  name,
  secondary,
  trailing,
  size = 16,
  grayscale,
  ...logo
}: Props) {
  return (
    <>
      <LeftIcon
        style={
          grayscale ? { filter: "grayscale(100%)", opacity: 0.4 } : undefined
        }
      >
        {logo.app ? (
          <ConnectorLogo app={logo.app} size={size} />
        ) : logo.template ? (
          <ConnectorLogo template={logo.template} size={size} />
        ) : null}
      </LeftIcon>
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {name}
        </Text>
        {secondary && (
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            {secondary}
          </Text>
        )}
      </div>
      {trailing}
    </>
  );
}

// A clickable connector row: navigates to `to`, or runs `onClick`, then closes
// the dropdown. `idle`/`hover` are trailing slots (idle shows at rest, hover on
// row hover) for affordances like a checkmark, open link, or remove button.
// With `openTo` set, holding Shift instead opens that URL (a peek at the
// connector's modal) and, while `shiftHeld`, reveals a hint icon on the right.
export function ConnectorOption({
  app,
  onClick,
  to,
  openTo,
  secondary,
  idle,
  hover,
  shiftHeld,
  close,
}: {
  app: AppInfo;
  onClick?: () => void;
  to?: string;
  openTo?: string;
  secondary?: React.ReactNode;
  idle?: React.ReactNode;
  hover?: React.ReactNode;
  shiftHeld?: boolean;
  close: () => void;
}) {
  return (
    <Row
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        playClick();
        close();
        if (e.shiftKey && openTo) {
          history.pushState(history.state, "", openTo);
          return;
        }
        if (to) history.pushState(history.state, "", to);
        else onClick?.();
      }}
    >
      <ConnectorRow
        app={app}
        name={app.name}
        secondary={secondary ?? createdByName(app.createdBy)}
        trailing={
          (idle || hover || (shiftHeld && openTo)) && (
            <>
              {idle && <span className="row-idle">{idle}</span>}
              {hover && (
                <div
                  className="row-hover"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {hover}
                </div>
              )}
              {shiftHeld && openTo && (
                <span className="row-open">
                  <Icon
                    name="arrow-up-right"
                    size={12}
                    color="var(--text-secondary)"
                  />
                </span>
              )}
            </>
          )
        }
      />
    </Row>
  );
}
